import { type InfiniteData, keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { Chat, ChatDetail, ChatSearchHit, ChatUploadResult } from "@/api/types";
import { cartKeys } from "@/features/cart/api";
import { apiUpload } from "@/lib/upload";
import { useAuth } from "@/stores/auth";

export type ChatFilter = "all" | "buying" | "selling" | "unread" | "offers" | "archived";
export const CHAT_FILTERS: { id: ChatFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "buying", label: "Buying" },
  { id: "selling", label: "Selling" },
  { id: "unread", label: "Unread" },
  { id: "offers", label: "Offers" },
  { id: "archived", label: "Archived" },
];

export const chatsKeys = {
  all: ["chats"] as const,
  lists: ["chats", "list"] as const,
  list: (filter: ChatFilter, search: string) => ["chats", "list", filter, search] as const,
  detail: (id: string) => ["chats", "detail", id] as const,
  unread: ["chats", "unread"] as const,
  search: (q: string) => ["chats", "search", q] as const,
};

type ChatPages = InfiniteData<{ results: Chat[]; next?: string | null; count?: number }>;

/** Total unread chats (buying + selling) for the tab-bar badge. */
export function useChatsUnread() {
  const authed = useAuth((s) => s.status === "authenticated");
  return useQuery({
    queryKey: chatsKeys.unread,
    queryFn: () => unwrap(api.GET("/api/v1/chats/unread/")),
    enabled: authed,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useChatList(filter: ChatFilter, search: string) {
  const authed = useAuth((s) => s.status === "authenticated");
  return useInfiniteQuery({
    queryKey: chatsKeys.list(filter, search),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/api/v1/chats/", { params: { query: { filter, search: search || undefined, page: pageParam } } })),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
    placeholderData: keepPreviousData,
    enabled: authed,
  });
}

export function useChatDetail(id: string | undefined) {
  return useQuery({
    queryKey: chatsKeys.detail(id ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/chats/{id}/", { params: { path: { id: id! } } })),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useChatSearch(q: string) {
  return useQuery({
    queryKey: chatsKeys.search(q),
    queryFn: () => unwrap(api.GET("/api/v1/chats/search/", { params: { query: { q } } })) as Promise<ChatSearchHit[]>,
    enabled: q.trim().length >= 2,
    staleTime: 15_000,
  });
}

/** Patch one conversation in every cached list and its detail, so swipes feel instant. */
function patchCaches(qc: ReturnType<typeof useQueryClient>, id: string, patch: Partial<Chat>) {
  qc.setQueriesData<ChatPages>({ queryKey: chatsKeys.lists }, (data) =>
    data
      ? { ...data, pages: data.pages.map((p) => ({ ...p, results: p.results.map((c) => (c.id === id ? { ...c, ...patch } : c)) })) }
      : data,
  );
  qc.setQueryData<ChatDetail>(chatsKeys.detail(id), (d) => (d ? { ...d, ...patch } : d));
}

export type ChatStatePatch = {
  is_pinned?: boolean;
  is_muted?: boolean;
  muted_until?: string | null;
  is_archived?: boolean;
  is_unread?: boolean;
};

export function useUpdateChatState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ChatStatePatch & { id: string }) =>
      unwrap(api.PATCH("/api/v1/chats/{id}/", { params: { path: { id } }, body })),
    onMutate: ({ id, ...body }) => {
      const patch: { -readonly [K in keyof Chat]?: Chat[K] } = {};
      if (body.is_pinned !== undefined) patch.is_pinned = body.is_pinned;
      if (body.is_muted !== undefined) patch.is_muted = body.is_muted;
      if (body.is_archived !== undefined) patch.is_archived = body.is_archived;
      if (body.is_unread !== undefined) {
        patch.is_marked_unread = body.is_unread;
        if (!body.is_unread) patch.unread = 0;
      }
      patchCaches(qc, id, patch);
    },
    onSuccess: (detail) => {
      qc.setQueryData(chatsKeys.detail(detail.id), detail);
      void qc.invalidateQueries({ queryKey: chatsKeys.lists });
      void qc.invalidateQueries({ queryKey: chatsKeys.unread });
    },
    onError: () => void qc.invalidateQueries({ queryKey: chatsKeys.all }),
  });
}

function useDetailMutation<V>(fn: (v: V) => Promise<ChatDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (detail) => {
      qc.setQueryData(chatsKeys.detail(detail.id), detail);
      void qc.invalidateQueries({ queryKey: chatsKeys.lists });
    },
  });
}

export const useClearChat = () =>
  useDetailMutation((id: string) => unwrap(api.POST("/api/v1/chats/{id}/clear/", { params: { path: { id } } })));

export const useBlockChat = () =>
  useDetailMutation(({ id, blocked }: { id: string; blocked: boolean }) =>
    blocked
      ? unwrap(api.POST("/api/v1/chats/{id}/block/", { params: { path: { id } } }))
      : unwrap(api.DELETE("/api/v1/chats/{id}/block/", { params: { path: { id } } })),
  );

export type ReportReason = "scam" | "abuse" | "spam" | "fake_item" | "other";
export const REPORT_REASONS: { id: ReportReason; label: string }[] = [
  { id: "scam", label: "Scam or fraud" },
  { id: "fake_item", label: "Fake or counterfeit item" },
  { id: "abuse", label: "Abuse or harassment" },
  { id: "spam", label: "Spam" },
  { id: "other", label: "Something else" },
];

export function useReportChat() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; reason: ReportReason; note?: string; message_id?: string | null }) =>
      unwrap(api.POST("/api/v1/chats/{id}/report/", { params: { path: { id } }, body })),
  });
}

export function useStartChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { listing_id?: string; product?: string; store?: string; message?: string; client_id?: string }) =>
      unwrap(api.POST("/api/v1/chats/", { body })),
    onSuccess: (detail) => {
      qc.setQueryData(chatsKeys.detail(detail.id), detail);
      void qc.invalidateQueries({ queryKey: chatsKeys.lists });
    },
  });
}

export function useOfferRespond() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ offerId, ...body }: { offerId: string; action: "accept" | "decline" | "counter"; amount?: number; note?: string }) =>
      unwrap(api.POST("/api/v1/offers/{id}/respond/", { params: { path: { id: offerId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatsKeys.lists });
      void qc.invalidateQueries({ queryKey: cartKeys.cart });
    },
  });
}

export function useOfferWithdraw() {
  return useMutation({
    mutationFn: (offerId: string) => unwrap(api.POST("/api/v1/offers/{id}/withdraw/", { params: { path: { id: offerId } } })),
  });
}

/** Upload a photo or voice note to send; reports progress (0–1). */
export function uploadChatFile(
  kind: "image" | "voice",
  file: Blob,
  {
    name,
    durationMs,
    waveform,
    onProgress,
    signal,
  }: { name?: string; durationMs?: number; waveform?: number[]; onProgress?: (f: number) => void; signal?: AbortSignal } = {},
): Promise<ChatUploadResult> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file, name ?? (kind === "voice" ? "voice.webm" : "photo.jpg"));
  if (durationMs) form.append("duration_ms", String(Math.round(durationMs)));
  if (waveform) form.append("waveform", JSON.stringify(waveform.map((v) => Math.round(v))));
  return apiUpload<ChatUploadResult>("/chats/uploads/", form, { onProgress, signal });
}
